import { useQuery as useBlogCarouselQuery } from "@faire/web-api/api/logged-out-homepage/blog-carousel/get-hooks";
import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import { Carousel } from "@faire/web/slate-labs/Carousel/Carousel";
import { Button } from "@faire/web/slate/Button";
import { Arrow } from "@faire/web/slate/icons/Arrow";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React from "react";

import * as carouselStyles from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/Carousel/carousel.css";
import * as buttonStyles from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/__internal__/TransparentButton/transparentButton.css";
import { BlogTile } from "@faire/retailer/views/home/logged-out-homepage/__internal__/components/BlogCarousel/__internal__/BlogTile";

import * as componentStyles from "./styles.css";

export const BlogCarousel: React.FC = () => {
  const ref = useDoActionWhenVisible(() =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView("blog_carousel")
  );
  const { data } = useBlogCarouselQuery({
    gcTime: Infinity,
    staleTime: Infinity,
  });

  if (!data?.carousel_tiles?.length) {
    return null;
  }

  const { title, cta_link_url, cta_link_text, carousel_tiles } = data;

  return (
    <Column
      className={componentStyles.container}
      data-test-id="blog-carousel"
      gap="4x"
      ref={ref}
    >
      <Row justify="space-between">
        <Typography
          variant={{
            mobileAndAbove: "displaySSerifRegular",
            desktopAndAbove: "displayMSerifRegular",
          }}
          style={{
            paddingRight: `${getSpacing("4x")}`,
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>
        <ResponsiveContentV2
          desktopAndAbove={
            <CTADesktopAndAbove
              ctaLink={cta_link_url}
              ctaLinkText={cta_link_text}
            />
          }
        />
      </Row>
      <Carousel
        items={carousel_tiles ?? []}
        renderItem={(blog) => <BlogTile blogTile={blog} />}
        freeFlow
        itemGap={16}
        showNavigationArrows={false}
        loop={false}
        className={carouselStyles.carousel}
        onVisible={() => {
          trackLoggedOutRetailersLoggedOutHomeRedesignView(
            "blog_carousel-onscroll"
          );
        }}
      />
      <ResponsiveContentV2
        tabletAndBelow={
          <CTATabletAndBelow
            ctaLink={cta_link_url}
            ctaLinkText={cta_link_text}
          />
        }
      />
    </Column>
  );
};

const CTADesktopAndAbove: React.FC<{
  ctaLink: string | undefined;
  ctaLinkText: string | undefined;
}> = ({ ctaLink, ctaLinkText }) => {
  return (
    <Button
      variant="tertiary"
      linkTo={ctaLink}
      className={componentStyles.cta}
      onClick={() => {
        trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
          "blog-carousel-cta",
          ctaLink ?? ""
        );
      }}
    >
      {ctaLinkText}
      <Spacer width="1x" />
      <Arrow direction="right" className={componentStyles.arrow} />
    </Button>
  );
};

const CTATabletAndBelow: React.FC<{
  ctaLink: string | undefined;
  ctaLinkText: string | undefined;
}> = ({ ctaLink, ctaLinkText }) => {
  return (
    <Button
      variant="secondary"
      style={{ width: "fit-content" }}
      linkTo={ctaLink}
      onClick={() => {
        trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
          "blog-carousel-cta",
          ctaLink ?? ""
        );
      }}
      className={buttonStyles.transparentButton}
    >
      {ctaLinkText}
    </Button>
  );
};
